import { Component, inject, Inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CarriersInfo } from '@views/common/models';
import { FlightJourney as FlightJourneyDs } from '@vueling-ng/vy-ds/src/lib/components/globals/flight-journey';
import {
  ProcessMessageSizeEnum,
  ProcessMessageTypeEnum
} from '@vueling-ng/vy-ds/src/lib/components/globals/process-message';
import { SIDEPANEL_DATA } from '@vueling-ng/vy-ds/src/lib/components/globals/side-panel';
import { FormatHoursPipe } from 'app/shared/pipes/format-hours.pipe';
import { Segment } from '../../models/booking-details.model';
import { FlightDetailCustomData } from './models/flight-detail-custom-data.model';

@Component({
  selector: 'app-flight-detail',
  templateUrl: './flight-detail.component.html',
  styleUrls: ['./flight-detail.component.scss']
})
export class FlightDetailComponent {
  public readonly ProcessMessageSizeEnum = ProcessMessageSizeEnum;
  public readonly ProcessMessageTypeEnum = ProcessMessageTypeEnum;
  public readonly formatHoursPipe: FormatHoursPipe = inject(FormatHoursPipe);
  public readonly translateService: TranslateService = inject(TranslateService);
  public readonly firstSegmentDs: FlightJourneyDs = this.fillDataConnectionSegmentInfo(
    this.data.journeyData.segments[0],
    !!this.data.journeyData.connectionTime,
    this.data.journeyData.isEarlyMorning,
    this.data.journeyData.isNightFlight
  )!;
  public readonly secondSegmentDs: FlightJourneyDs | undefined = this.fillDataConnectionSegmentInfo(
    this.data.journeyData.segments[1],
    !!this.data.journeyData.connectionTime,
    this.data.journeyData.isEarlyMorning,
    this.data.journeyData.isNightFlight
  );
  public readonly firstSegment: Segment = this.data.journeyData.segments[0];
  public readonly secondSegment: Segment | undefined = this.data.journeyData.segments[1];

  constructor(@Inject(SIDEPANEL_DATA) public data: FlightDetailCustomData) {}

  public getOperatorIcon(segment: Segment): string | undefined {
    return CarriersInfo[segment.codeShareCarrierName ?? segment.carrierName]?.operatorIcon;
  }

  private isPlusDay(dateOrigin: Date, dateDestination: Date): boolean {
    return (
      dateOrigin.getFullYear() !== dateDestination.getFullYear() ||
      dateOrigin.getMonth() !== dateDestination.getMonth() ||
      dateOrigin.getDate() !== dateDestination.getDate()
    );
  }

  private fillDataConnectionSegmentInfo(
    segment: Segment,
    hasConnection: boolean,
    isEarlyMorning: boolean,
    isNightFlight: boolean
  ): FlightJourneyDs | undefined {
    if (segment === undefined) {
      return undefined;
    }
    const origin =
      this.data.stations.find((station) => station.stationCode === segment.iataOrigin)?.fullName ?? segment.iataOrigin;
    const destination =
      this.data.stations.find((station) => station.stationCode === segment.iataDestination)?.fullName ??
      segment.iataDestination;
    const segmentTime = segment.arrivalDate.getTime() - segment.departureDate.getTime();
    const isPlusDay = this.isPlusDay(segment.departureDate, segment.arrivalDate);
    return {
      ...segment,
      dateOrigin: segment.departureDate,
      dateDestination: segment.arrivalDate,
      placeOrigin: origin,
      placeDestination: destination,
      airportOrigin: origin,
      airportDestination: destination,
      tripTime: this.formatHoursPipe.transform(
        `${Math.floor(segmentTime / (1000 * 60 * 60))}:${Math.floor(segmentTime % (1000 * 60 * 60)) / (1000 * 60)}`
      ),
      plusDay: isPlusDay ? 'booking_details_component.flight_journey_summary_component.plus_one_day' : undefined,
      terminalOrigin: segment.terminalOrigin ?? '1',
      terminalDestination: segment.terminalDestination ?? '1',
      terminalLabel: 'booking_details_component.flight_journey_summary_component.terminal',
      flightJourneyLabels: {
        earlyMorningFlightLabel: isEarlyMorning
          ? (this.translateService.instant(
              'booking_details_component.flight_journey_summary_component.early_morning_flight',
              {
                initialDay: segment.departureDate.getDate(),
                weekday: segment.arrivalDate.toLocaleDateString(this.translateService.currentLang, { weekday: 'long' })
              }
            ) as string)
          : undefined,
        nightFlightLabel: isNightFlight
          ? (this.translateService.instant('booking_details_component.night_flight', {
              initialDay: segment.departureDate.getDate(),
              finalDate: segment.arrivalDate.getDate()
            }) as string)
          : undefined,
        connectionLabel: hasConnection
          ? 'booking_details_component.flight_journey_summary_component.connection'
          : 'booking_details_component.flight_journey_summary_component.direct',
        plusDayLabel: isPlusDay
          ? 'booking_details_component.flight_journey_summary_component.plus_one_day_label'
          : undefined
      }
    };
  }
}
